import './PageSearch.scss';
import cn from 'classnames';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { MdSearch } from 'react-icons/md';
import { highlightMatches, useFilteredList } from '@src/utils/search';
import { FullBpmLocation } from '../../../utils/routing/location-constants';
import { buildSearchLocations, useNavLocations } from '../../../utils/routing/location-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useBpmNavigate } from '../../../utils/routing/useBpmNavigate';
import { Button } from '../../Button';
import { Appearance, Background, Placement, Size } from '../../designConstants';
import { IconButton } from '../../IconButton';
import { SearchField } from '../../SearchField/SearchField';
import { EmptySearch } from '../EmptySearch/EmptySearch';
import { NAV_POPOVER_CONFIG, NavMenuKey } from '../navbar-constants';
import { useNavHoverOpen, useNavTopMenu } from '../useNavBarPopover';

// Nav routing already flattens searchNames per level, so joining with a space
// gives the shared util a single per-location haystack.
const locationHaystack = (loc: FullBpmLocation): string => loc.searchNames.join(' ');

export const PageSearch = (): ReactNode => {
  const [showMenu, setShowMenu] = useNavTopMenu(NavMenuKey.PAGE_SEARCH);
  const { hoverHandlers, consumeHoverOpen } = useNavHoverOpen(showMenu, () => setShowMenu(true));
  const { globalFlatLocations, clientFlatLocations } = useNavLocations();
  const { cid } = useCompanyInfo();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const searchLocations = useMemo(
    () =>
      cid
        ? buildSearchLocations(clientFlatLocations, globalFlatLocations)
        : buildSearchLocations(globalFlatLocations, clientFlatLocations),
    [globalFlatLocations, clientFlatLocations, cid]
  );

  const filteredLocations = useFilteredList(searchLocations, searchQuery, {
    keys: [locationHaystack]
  });

  return (
    <>
      <IconButton
        {...hoverHandlers}
        data-testid='page-search-button'
        className={cn('page-search', { active: showMenu })}
        appearance={Appearance.EMPTY}
        background={Background.DARK}
        onClick={() => {
          hoverHandlers.onClick();
          if (consumeHoverOpen() && showMenu) return;
          setShowMenu(!showMenu);
        }}
        aria-label='Search pages'
        ref={buttonRef}
      >
        <MdSearch />
      </IconButton>
      <Overlay
        target={buttonRef}
        show={showMenu}
        placement={Placement.RIGHT_START}
        popperConfig={NAV_POPOVER_CONFIG}
      >
        {props => (
          <Popover id='page-search-popover' className='nav-popover page-search-popover' {...props}>
            <Popover.Header>
              <SearchField
                size={Size.LG}
                id='page-search-search'
                onChange={e => {
                  setSearchQuery(e.currentTarget.value);
                }}
                value={searchQuery}
                background={Background.DARK}
                placeholder='Search all pages'
                autoFocus
              />
            </Popover.Header>
            <Popover.Body>
              <PageSearchMenu
                locations={filteredLocations}
                searchQuery={searchQuery}
                resetSearchQuery={() => setSearchQuery('')}
              />
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
};

interface PageSearchMenuProps {
  locations: readonly FullBpmLocation[];
  searchQuery: string;
  resetSearchQuery: () => void;
}

const PageSearchMenu = ({ locations, searchQuery, resetSearchQuery }: PageSearchMenuProps) => {
  if (!searchQuery) {
    return <EmptySearch icon={MdSearch} label='Start typing to search' />;
  }

  if (locations.length === 0) {
    return <EmptySearch />;
  }

  if (locations.length > 25) {
    return <EmptySearch icon={MdSearch} label='Keep typing to search' />;
  }

  return (
    <div className='nav-group-menu'>
      {locations.map(loc => (
        <PageSearchMenuItem
          key={loc.fullPath}
          location={loc}
          searchQuery={searchQuery}
          resetSearchQuery={resetSearchQuery}
        />
      ))}
    </div>
  );
};

interface PageSearchMenuItemProps {
  location: FullBpmLocation;
  searchQuery: string;
  resetSearchQuery: () => void;
}

const PageSearchMenuItem = ({
  location,
  searchQuery,
  resetSearchQuery
}: PageSearchMenuItemProps) => {
  const navigate = useBpmNavigate();
  const { prettyName, searchNames, type, fullPath } = location;

  return (
    <Button
      size={Size.LG}
      className='page-search-menu-item'
      appearance={Appearance.EMPTY}
      background={Background.DARK}
      onClick={() => navigate({ type, path: fullPath, prettyName, cleanup: resetSearchQuery })}
    >
      <div className='page-name'>{prettyName}</div>
      <div className='page-breadcrumb'>
        {highlightMatches(searchNames.join(' > '), searchQuery)}
      </div>
    </Button>
  );
};
